import { useState, useEffect, useCallback, useRef } from 'react'
import type { JourneyIntroSentence } from '@/data/journeys/types'

interface JourneyIntroProps {
  sentences: JourneyIntroSentence[]
  journeySlug: string
}

export default function JourneyIntro({ sentences, journeySlug }: JourneyIntroProps) {
  const INTRO_KEY = `ws_journey_${journeySlug}_intro_seen`

  const shouldShowIntro = (): boolean => {
    if (typeof window === 'undefined') return false
    // Skip intro if already seen this session
    if (sessionStorage.getItem(INTRO_KEY)) {
      return false
    }
    return true
  }

  const [isVisible, setIsVisible] = useState(() => shouldShowIntro())
  const [isFadingOut, setIsFadingOut] = useState(false)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [textState, setTextState] = useState<'visible' | 'fading' | 'hidden'>('hidden')
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const skipIntro = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    setIsFadingOut(true)
    sessionStorage.setItem(INTRO_KEY, 'true')
    setTimeout(() => {
      setIsVisible(false)
    }, 800)
  }, [INTRO_KEY])

  useEffect(() => {
    if (!isVisible) return

    const showNextSentence = () => {
      if (currentIndex >= sentences.length) {
        setIsFadingOut(true)
        sessionStorage.setItem(INTRO_KEY, 'true')
        setTimeout(() => {
          setIsVisible(false)
        }, 800)
        return
      }

      setTextState('fading')

      timeoutRef.current = setTimeout(() => {
        setTextState('visible')
        setCurrentIndex((prev) => prev + 1)
        timeoutRef.current = setTimeout(showNextSentence, 2200)
      }, 400)
    }

    timeoutRef.current = setTimeout(showNextSentence, 500)

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [isVisible, sentences.length, INTRO_KEY]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!isVisible) return null

  const currentSentence = sentences[Math.max(0, currentIndex - 1)]

  return (
    <div
      className={`
        fixed inset-0 flex items-center justify-center z-[9999] flex-col
        transition-opacity duration-[800ms] ease-out
        ${isFadingOut ? 'opacity-0 pointer-events-none' : 'opacity-100'}
      `}
      style={{ background: '#0a0d12' }}
    >
      {/* Intro Text */}
      <div
        className={`
          font-serif text-4xl text-center max-w-[700px] px-6 leading-[1.4]
          transition-all duration-[800ms] ease-out
          ${textState === 'visible' ? 'opacity-100 translate-y-0' : ''}
          ${textState === 'fading' ? 'opacity-0 -translate-y-5' : ''}
          ${textState === 'hidden' ? 'opacity-0 translate-y-5' : ''}
        `}
        style={{ color: '#e8e6e1' }}
      >
        {currentIndex > 0 && currentSentence && (
          <>
            {currentSentence.text}
            <span className="whitespace-nowrap inline" style={{ color: '#14b8a6' }}>
              {currentSentence.highlight}
            </span>
            .
          </>
        )}
      </div>

      {/* Progress Dots */}
      <div className="absolute bottom-[60px] flex gap-2">
        {sentences.map((_, index) => (
          <div
            key={index}
            className="w-2 h-2 rounded-full transition-all duration-300 ease-out"
            style={{
              background: index === currentIndex - 1 || (currentIndex === 0 && index === 0)
                ? '#14b8a6'
                : 'rgba(255,255,255,0.2)',
              boxShadow: index === currentIndex - 1 || (currentIndex === 0 && index === 0)
                ? '0 0 12px rgba(20,184,166,0.5)'
                : 'none'
            }}
          />
        ))}
      </div>

      {/* Skip Button */}
      <button
        onClick={skipIntro}
        className="
          absolute bottom-[100px] bg-transparent border px-6 py-2.5 rounded-md text-sm font-sans cursor-pointer
          transition-all duration-300 ease-out
          hover:bg-white/5
        "
        style={{
          borderColor: 'rgba(255,255,255,0.2)',
          color: '#a8a5a0',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = 'rgba(255,255,255,0.4)'
          e.currentTarget.style.color = '#e8e6e1'
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)'
          e.currentTarget.style.color = '#a8a5a0'
        }}
      >
        Skip
      </button>
    </div>
  )
}
